import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

/**
 * Searchable + creatable single-select combobox built on Command + Popover.
 *
 * - Type to filter the existing options.
 * - When the typed text doesn't match an existing option, a "Create <text>"
 *   action appears so the user can add a brand-new value without leaving the form.
 *
 * Props
 *  - value           : currently selected value (string)
 *  - onValueChange   : (value) => void
 *  - options         : array of { value, label } (label optional -> falls back to value)
 *  - onCreate        : (newValue) => void  (fires when a new value is created)
 *  - allowCreate     : boolean (default true)
 *  - placeholder / searchPlaceholder / emptyText
 *  - error           : boolean (applies danger border)
 *  - testId          : base data-testid for the trigger + derived children
 */
export function CreatableCombobox({
  value = "",
  onValueChange,
  options = [],
  onCreate,
  allowCreate = true,
  placeholder = "Select\u2026",
  searchPlaceholder = "Search or type to add\u2026",
  emptyText = "No matches.",
  createLabel = "Create",
  disabled = false,
  error = false,
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const norm = (s) => (s || "").trim().toLowerCase();
  const q = query.trim();
  const exactExists = options.some((o) => norm(o.value) === norm(q));
  const selected = options.find((o) => o.value === value);
  const selectedLabel = selected ? (selected.label || selected.value) : (value || "");

  const pick = (val) => {
    onValueChange && onValueChange(val);
    setOpen(false);
    setQuery("");
  };

  const create = () => {
    if (!q) return;
    onCreate && onCreate(q);
    pick(q);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (disabled) return; setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm ring-offset-background transition-colors duration-150",
            "hover:bg-[hsl(var(--surface-2))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-[hsl(var(--danger))]",
            className
          )}
        >
          <span className={cn("truncate", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[220px] p-0"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <Command
          filter={(itemValue, search) => {
            if (itemValue === "__create__") return 1; // always keep the create row visible
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && q ? null : <span className="text-muted-foreground">{emptyText}</span>}
            </CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.value}
                  onSelect={() => pick(o.value)}
                  data-testid={testId ? `${testId}-option` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{o.label || o.value}</span>
                </CommandItem>
              ))}
              {allowCreate && q && !exactExists && (
                <CommandItem
                  value="__create__"
                  onSelect={create}
                  className="text-[hsl(var(--primary))]"
                  data-testid={testId ? `${testId}-create` : undefined}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="truncate">{createLabel} &ldquo;{q}&rdquo;</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
